import {
  createUniversalReporter,
  createSemanticMetrics,
  MetricTypes,
  Time,
} from '@spotify-internal/semantic-metrics';
import type { SemanticMetrics } from '@spotify-internal/semantic-metrics';
import { BrowserMetrics } from '@spotify-internal/semantic-metrics/cjs/browser';
import { getCLS, getFID, getLCP } from 'web-vitals';
import ttiPolyfill from 'tti-polyfill';

let sendMetric: SemanticMetrics['sendMetric'];

export function configureClientInstrumentation() {
  /*
   * as per its event definition (https://ghe.spotify.net/datainfra/event-definitions/blob/master/events/SemanticMetricBrowserNonAuth.yaml)
   * only strings are allowed within tags.
   */
  const market = window.location.pathname.match(/^\/[a-z]{2}(-[a-z]{2})?\//i);
  const commonTags = {
    origin: window.location.origin,
    path: window.location.pathname.replace(
      /^\/[a-z]{2}(-[a-z]{2})?\//i,
      '/{spotifyMarket}/',
    ),
    market:
      market && typeof market[0] === 'string'
        ? market[0].replace(/\//g, '')
        : '',
    marketPath: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    device: 'onorientationchange' in window ? 'mobile' : 'desktop',
  };

  sendMetric = createSemanticMetrics({
    key: 'www-marketing',
    reporter: createUniversalReporter(),
    dev: false,
  }).sendMetric;

  function reportWebVitals(metric: any) {
    sendMetric(BrowserMetrics.getWebVitalsMetric(metric, commonTags));
  }

  function getUserConnectionType() {
    if (!window.navigator.connection) {
      return 'unknown';
    }

    return window.navigator.connection.effectiveType;
  }

  // log users with JS enabled
  sendMetric({
    metric_type: MetricTypes.COUNTER,
    what: 'page-load',
    value: 1,
    tags: commonTags,
  });

  // log users connection speed
  sendMetric({
    metric_type: MetricTypes.COUNTER,
    what: 'connection',
    value: 1,
    tags: Object.assign({}, commonTags, {
      effective_type: getUserConnectionType(),
    }),
  });

  BrowserMetrics.getPageLoadTime().then(time => {
    sendMetric({
      metric_type: MetricTypes.TIMER,
      what: 'time-to-page-load-nanoseconds',
      value: Time.fromMillis(time).asNanos(),
      tags: commonTags,
    });
  });

  BrowserMetrics.getTimeToFirstPaint().then(time => {
    sendMetric({
      metric_type: MetricTypes.TIMER,
      what: 'time-to-first-paint-nanoseconds',
      value: Time.fromMillis(time).asNanos(),
      tags: commonTags,
    });
  });

  BrowserMetrics.getTimeToFirstContentfulPaint().then(time => {
    sendMetric({
      metric_type: MetricTypes.TIMER,
      what: 'time-to-first-contentful-paint-nanoseconds',
      value: Time.fromMillis(time).asNanos(),
      tags: commonTags,
    });
  });

  ttiPolyfill.getFirstConsistentlyInteractive().then(time => {
    if (time) {
      sendMetric({
        metric_type: MetricTypes.TIMER,
        what: 'time-to-interactive-nanoseconds',
        value: Time.fromMillis(time).asNanos(),
        tags: commonTags,
      });
    }
  });

  getCLS(reportWebVitals);
  getFID(reportWebVitals);
  getLCP(reportWebVitals);
}
